import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PlannerDeleteResponse,
  PlannerBacklinks,
  PlannerCalendarTaskList,
  PlannerNote,
  PlannerNoteAttachment,
  PlannerNoteList,
  PlannerTask,
  PlannerTaskList,
  PlannerTaskReminderList,
  PlannerTaskSyncResult,
  type PlannerNoteCreate,
  type PlannerNoteUpdate,
  type PlannerBacklinkTargetKind,
  type PlannerTaskCreate,
  type PlannerTaskPriority,
  type PlannerTaskReminderAction,
  type PlannerTaskSyncRequest,
  type PlannerTaskUpdate,
  type PlannerTaskView,
} from '@email/shared';
import { ApiError, api } from './client.js';
import {
  plannerInvalidations,
  plannerKeys,
} from './query-cache.js';

export function usePlannerTasks(options: {
  query: string;
  status: 'open' | 'completed' | 'all';
  priority?: PlannerTaskPriority;
  view: PlannerTaskView;
}) {
  const normalized = options.query.trim();
  const bounds = localTaskBounds();
  return useQuery({
    queryKey: plannerKeys.taskList(
      normalized,
      options.status,
      options.priority ?? null,
      options.view,
      bounds.dayStart,
    ),
    queryFn: () => {
      const query = new URLSearchParams({
        status: options.status,
        view: options.view,
        dayStart: String(bounds.dayStart),
        dayEnd: String(bounds.dayEnd),
        upcomingEnd: String(bounds.upcomingEnd),
      });
      if (normalized) query.set('query', normalized);
      if (options.priority) query.set('priority', options.priority);
      return api(`/api/tasks?${query.toString()}`, {
        schema: PlannerTaskList,
      });
    },
  });
}

export function usePlannerTask(id: number | null) {
  return useQuery({
    queryKey: plannerKeys.task(id ?? 0),
    queryFn: () =>
      api(`/api/tasks/${id}`, {
        schema: PlannerTask,
      }),
    enabled: id !== null,
  });
}

export function usePlannerReminders(enabled = true) {
  return useQuery({
    queryKey: plannerKeys.reminders,
    queryFn: () =>
      api('/api/tasks/reminders', { schema: PlannerTaskReminderList }),
    enabled,
    refetchInterval: 30_000,
  });
}

export function usePlannerCalendarTasks(
  start: number,
  end: number,
  enabled = true,
) {
  return useQuery({
    queryKey: plannerKeys.calendarTasks(start, end),
    queryFn: () => {
      const query = new URLSearchParams({
        start: String(start),
        end: String(end),
      });
      return api(`/api/tasks/calendar?${query.toString()}`, {
        schema: PlannerCalendarTaskList,
      });
    },
    enabled,
  });
}

export function usePlannerBacklinks(
  kind: PlannerBacklinkTargetKind,
  id: number,
  enabled = true,
) {
  return useQuery({
    queryKey: plannerKeys.backlinks(kind, id),
    queryFn: () =>
      api(`/api/planner/backlinks/${kind}/${id}`, {
        schema: PlannerBacklinks,
      }),
    enabled,
  });
}

export function usePlannerNotes(options: {
  query: string;
  pinned: 'all' | 'pinned';
  tag?: string;
}) {
  const normalized = options.query.trim();
  return useQuery({
    queryKey: plannerKeys.noteList(
      normalized,
      options.pinned,
      options.tag ?? null,
    ),
    queryFn: () => {
      const query = new URLSearchParams({ pinned: options.pinned });
      if (normalized) query.set('query', normalized);
      if (options.tag) query.set('tag', options.tag);
      return api(`/api/notes?${query.toString()}`, {
        schema: PlannerNoteList,
      });
    },
  });
}

export function usePlannerNote(id: number | null) {
  return useQuery({
    queryKey: plannerKeys.note(id ?? 0),
    queryFn: () =>
      api(`/api/notes/${id}`, {
        schema: PlannerNote,
      }),
    enabled: id !== null,
  });
}

export function useCreatePlannerTask() {
  return plannerMutation((body: PlannerTaskCreate) =>
    api('/api/tasks', { method: 'POST', body, schema: PlannerTask }),
  );
}

export function useUpdatePlannerTask() {
  return plannerMutation(
    ({ id, body }: { id: number; body: PlannerTaskUpdate }) =>
      api(`/api/tasks/${id}`, {
        method: 'PATCH',
        body,
        schema: PlannerTask,
      }),
  );
}

export function useDeletePlannerTask() {
  return plannerMutation((id: number) =>
    api(`/api/tasks/${id}`, {
      method: 'DELETE',
      schema: PlannerDeleteResponse,
    }),
  );
}

export function usePlannerReminderAction() {
  return plannerMutation(
    ({
      id,
      body,
    }: {
      id: number;
      body: PlannerTaskReminderAction;
    }) =>
      api(`/api/tasks/${id}/reminder`, {
        method: 'POST',
        body,
        schema: PlannerTask,
      }),
  );
}

export function useSyncPlannerTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PlannerTaskSyncRequest = {}) =>
      api('/api/tasks/sync', {
        method: 'POST',
        body,
        schema: PlannerTaskSyncResult,
      }),
    onSettled: () => plannerInvalidations.withCalendar.invalidate(queryClient),
  });
}

export function useCreatePlannerNote() {
  return plannerMutation((body: PlannerNoteCreate) =>
    api('/api/notes', { method: 'POST', body, schema: PlannerNote }),
  );
}

export function useUpdatePlannerNote() {
  return plannerMutation(
    ({ id, body }: { id: number; body: PlannerNoteUpdate }) =>
      api(`/api/notes/${id}`, {
        method: 'PATCH',
        body,
        schema: PlannerNote,
      }),
  );
}

export function useDeletePlannerNote() {
  return plannerMutation((id: number) =>
    api(`/api/notes/${id}`, {
      method: 'DELETE',
      schema: PlannerDeleteResponse,
    }),
  );
}

export function useUploadPlannerNoteAttachment() {
  return plannerMutation(
    async ({ noteId, file }: { noteId: number; file: File }) => {
      const data = new FormData();
      data.append('attachment', file, file.name);
      const response = await fetch(`/api/notes/${noteId}/attachments`, {
        method: 'POST',
        credentials: 'same-origin',
        body: data,
      });
      const raw = await response.text();
      let body: unknown = null;
      if (raw) {
        try {
          body = JSON.parse(raw) as unknown;
        } catch {
          body = raw;
        }
      }
      if (!response.ok) {
        const message =
          body !== null && typeof body === 'object' && 'error' in body
            ? String((body as { error: unknown }).error)
            : `Attachment upload failed (${response.status})`;
        throw new ApiError(response.status, message, body);
      }
      return PlannerNoteAttachment.parse(body);
    },
  );
}

export function useDeletePlannerNoteAttachment() {
  return plannerMutation(
    ({ noteId, attachmentId }: { noteId: number; attachmentId: number }) =>
      api(`/api/notes/${noteId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        schema: PlannerDeleteResponse,
      }),
  );
}

export async function fetchPlannerNotesExport(
  format: 'markdown' | 'json',
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`/api/notes/export?format=${format}`, {
    credentials: 'same-origin',
  });
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Preserve the status text for non-JSON reverse-proxy errors.
    }
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `${response.status} ${response.statusText}`;
    throw new ApiError(response.status, message, body);
  }
  const disposition = response.headers.get('content-disposition') ?? '';
  const fallback = format === 'json' ? 'wintermute-notes.json' : 'wintermute-notes.md';
  return {
    blob: await response.blob(),
    filename: disposition.match(/filename="([^"]+)"/i)?.[1] ?? fallback,
  };
}

function plannerMutation<TVariables, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => plannerInvalidations.all.invalidate(queryClient),
  });
}

function localTaskBounds(): {
  dayStart: number;
  dayEnd: number;
  upcomingEnd: number;
} {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  const upcoming = new Date(end);
  upcoming.setDate(upcoming.getDate() + 7);
  return {
    dayStart: start.getTime(),
    dayEnd: end.getTime(),
    upcomingEnd: upcoming.getTime(),
  };
}
